import React, { Fragment, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import { GetMyTeamMemberInvites } from '../../../../slices/team-member-invites/get-my-team-member-invites.thunk';
import { MyInvitedTeamMembersMenuItem } from '../items/MyInvitedTeamMembersMenuItem';
import { LeftMenuUser } from '../LeftMenuUser';
import { CurrentUserLimits } from '../../../../slices/current-user/current-user.state';
import {
    PRACTICE_STATUS_ENUM,
    AssociateMemberForNonprofitStatus,
} from '../../../../utils/constants';
import { VenueId } from '../../../../venues/venues.types';
import { useSelectedVenueId } from '../../../../slices/venue/use-selected-venue';
import { getMappedAssosiateMemberRights } from '../../../../utils/associateMembersConstants';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { FeatureFlag } from '../../../../containers/FeatureFlags/FeatureFlag';
import { FeatureFlagsEnum } from '../../../../containers/FeatureFlags/FeatureFlags.enum';
import { HomeMenuItem } from '../items/HomeMenuItem';
import { MyAccountMenuItem } from '../items/MyAccountMenuItem';
import { NotificationSettingsMenuItem } from '../items/NotificationSettingsMenuItem';
import { MyNonprofitMenuItem } from '../items/MyNonprofitMenuItem';
import { MyPracticesMenuItem } from '../items/MyPracticesMenuItem';
import { MarketplaceDashboardMenuItem } from '../items/MarketplaceDashboardMenuItem';
import { MarketplaceTeamMembersMenuItem } from '../items/MarketplaceTeamMembersMenuItem';
import { MyGroupsMenuItem } from '../items/MyGroupsMenuItem';
import { MyReferralHubMenuItem } from '../items/MyReferralHubMenuItem';
import { MyEngagementsMenuItem } from '../items/MyEngagementsMenuItem';
import { MyDealsMenuItem } from '../items/MyDealsMenuItem';
import { ObservingDealsMenuItem } from '../items/ObservingDealsMenuItem';
import { isVisibleInVenue } from '../../../../slices/venue/venue-menu-filter';
import { isCharityAffiliatedMember } from '../member-access';

const HEADING = 'app-sidebar__heading vertical-nav-menu';
const HEADING_CUSTOM = 'app-sidebar__heading vertical-nav-menu custom';

export interface SecondaryUserMenuProps {
    user: LeftMenuUser;
    limits: CurrentUserLimits;
}

export const SecondaryUserMenu = (props: SecondaryUserMenuProps) => {
    const { user, limits } = props;
    const selectedVenue = useSelectedVenueId();
    const practices = user.practices || [];
    const isAnyPracticeApproved =
        practices.filter((p) => p.status === PRACTICE_STATUS_ENUM.Approved)
            .length > 0;
    const isMarketplaceBuyer = !!user.isMarketplaceBuyer;
    const isMarketplaceBroker = !!user.isMarketplaceBroker;
    const associateMember = user.associateMember || [];
    const nonprofitMapping = associateMember[0]?.nonprofitMapping;
    const engagements = user.engagements || [];

    const isActingPrimaryBuyer =
        isMarketplaceBuyer &&
        associateMember.some((am: any) =>
            am.associateMemberMarketplaceMappings?.some(
                (m: any) => m.isGeneralSettings && m.isActingPrimaryBuyer
            )
        );

    const dispatch = useDispatch() as any;
    const canInviteTeamMembers = useStateSelector(
        (state) => state.teamMemberInvites.canInvite
    );
    const invitedTeamMembersCount = useStateSelector(
        (state) => state.teamMemberInvites.members.length
    );

    useEffect(() => {
        dispatch(GetMyTeamMemberInvites());
    }, [dispatch]);

    const invitedTeamMembersVenues = isMarketplaceBuyer
        ? [VenueId.Connect]
        : [VenueId.Connect, VenueId.Grow];
    const showInvitedTeamMembers =
        isVisibleInVenue(invitedTeamMembersVenues, selectedVenue) &&
        !isActingPrimaryBuyer &&
        (canInviteTeamMembers || invitedTeamMembersCount > 0);

    const isAssociateHaveAccessToSeeReferralHub =
        associateMember.length > 0 &&
        (associateMember[0]?.practices?.some(
            (p: any) =>
                getMappedAssosiateMemberRights(p.acceptedAccessRights)
                    .referralPartnerExtension
        ) ||
            (getMappedAssosiateMemberRights(
                nonprofitMapping?.associateMemberAccessRights
            ).referralPartnerExtension &&
                nonprofitMapping?.associateMemberStatus ===
                    AssociateMemberForNonprofitStatus.APPROVED));

    const showNonprofit =
        nonprofitMapping?.associateMemberStatus ===
            AssociateMemberForNonprofitStatus.APPROVED &&
        !nonprofitMapping?.isMemberBelongToFormerNonprofitAdmin;

    const hasSellerEngagement =
        (practices.some((p: any) => p.isInEngagement) ||
            associateMember.some((am: any) =>
                am.associateMemberMarketplaceMappings?.some(
                    (m: any) =>
                        m.isSellerSide &&
                        m.currentSellerTMPermissions?.length > 0
                )
            )) &&
        engagements.some((e: any) => e.party === TimelineParty.Seller);

    return (
        <Fragment>
            <div id="left-menu" />
            <div>
                {!isCharityAffiliatedMember(user) &&
                    !(
                        isMarketplaceBuyer &&
                        isVisibleInVenue([VenueId.Connect], selectedVenue)
                    ) && <HomeMenuItem className={HEADING} />}
                <MyAccountMenuItem />
                <FeatureFlag flag={FeatureFlagsEnum.FF_Notifications}>
                    <NotificationSettingsMenuItem />
                </FeatureFlag>
                {showInvitedTeamMembers && !isMarketplaceBuyer && (
                    <MyInvitedTeamMembersMenuItem />
                )}

                {showNonprofit ? (
                    <MyNonprofitMenuItem charityId={nonprofitMapping.nonprofitId} />
                ) : (
                    isVisibleInVenue([VenueId.Grow], selectedVenue) && (
                        <MyPracticesMenuItem
                            practices={user.practices}
                            headingClassName={HEADING}
                            showHeading={!isMarketplaceBuyer}
                        />
                    )
                )}

                {!isMarketplaceBuyer && isVisibleInVenue([VenueId.Grow], selectedVenue) && (
                    <MyGroupsMenuItem
                        summary={user}
                        limits={limits}
                        headingClassName={
                            isAnyPracticeApproved
                                ? 'app-sidebar__heading vertical-nav-menu hidden-background'
                                : 'app-sidebar__heading vertical-nav-menu custom hidden-background'
                        }
                    />
                )}

                {isVisibleInVenue([VenueId.Share], selectedVenue) && isAssociateHaveAccessToSeeReferralHub && (
                    <MyReferralHubMenuItem
                        isReferralPartner={user.isReferral}
                        className={
                            isAnyPracticeApproved ||
                            isAssociateHaveAccessToSeeReferralHub
                                ? HEADING
                                : HEADING_CUSTOM
                        }
                    />
                )}

                {isVisibleInVenue([VenueId.Connect], selectedVenue) && isMarketplaceBuyer && (
                    <MarketplaceDashboardMenuItem />
                )}

                {isVisibleInVenue([VenueId.Connect], selectedVenue) && isActingPrimaryBuyer && (
                    <MarketplaceTeamMembersMenuItem />
                )}

                {showInvitedTeamMembers && isMarketplaceBuyer && (
                    <MyInvitedTeamMembersMenuItem />
                )}

                {isVisibleInVenue([VenueId.Connect], selectedVenue) && hasSellerEngagement && (
                    <MyEngagementsMenuItem engagements={engagements} />
                )}
            </div>

            {isVisibleInVenue([VenueId.Connect], selectedVenue) && isMarketplaceBroker &&
                engagements.some((e: any) => e.party === TimelineParty.Broker) && (
                    <MyDealsMenuItem engagements={engagements} />
                )}
            {isVisibleInVenue([VenueId.Connect], selectedVenue) && isMarketplaceBroker &&
                engagements.some(
                    (e: any) => e.party === TimelineParty.Observer
                ) && <ObservingDealsMenuItem engagements={engagements} />}
        </Fragment>
    );
};
